import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Tooltip, IconButton, Typography, Box } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { getFileType, getFileNameWithoutExtension } from "@utils/fileUtils";
import type { Attachment } from "@typings/entities/attachments";

type Size = "sm" | "md" | "lg" | "xl";

const sizeClasses: Record<Size, string> = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
  xl: "w-48 h-48",
};

interface AttachmentPreviewProps {
  attachments: Attachment[];
  size?: Size;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
  };
  isLoading?: boolean;
}

function AttachmentPreview({
  attachments,
  size = "md",
  pagination,
}: AttachmentPreviewProps) {
  const [activeAttachment, setActiveAttachment] = useState<Attachment | null>(
    null,
  );
  const [currentAttachmentIndex, setCurrentAttachmentIndex] =
    useState<number>(0);

  const handleDownload = (attachment: Attachment) => {
    const link = document.createElement("a");
    link.href = attachment.url;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openAttachmentModal = (attachment: Attachment, index: number) => {
    setActiveAttachment(attachment);
    setCurrentAttachmentIndex(index);
  };

  const navigateAttachment = useCallback(
    (direction: "next" | "prev") => {
      const totalAttachments = attachments.length;
      let newIndex = currentAttachmentIndex;

      if (direction === "next") {
        newIndex = currentAttachmentIndex + 1;

        if (newIndex >= totalAttachments) {
          if (pagination && pagination.currentPage < pagination.totalPages) {
            pagination.onPageChange(pagination.currentPage + 1);
            setCurrentAttachmentIndex(0);
            return;
          } else {
            newIndex = 0;
          }
        }
      } else {
        newIndex = currentAttachmentIndex - 1;

        if (newIndex < 0) {
          if (pagination && pagination.currentPage > 1) {
            pagination.onPageChange(pagination.currentPage - 1);
            return;
          } else {
            newIndex = totalAttachments - 1;
          }
        }
      }

      setCurrentAttachmentIndex(newIndex);
      setActiveAttachment(attachments[newIndex]);
    },
    [currentAttachmentIndex, attachments, pagination],
  );

  const handleKeyNavigation = useCallback(
    (event: KeyboardEvent) => {
      if (!activeAttachment) return;

      if (event.key === "ArrowLeft") {
        navigateAttachment("prev");
      } else if (event.key === "ArrowRight") {
        navigateAttachment("next");
      } else if (event.key === "Escape") {
        setActiveAttachment(null);
      }
    },
    [activeAttachment, navigateAttachment],
  );

  useEffect(() => {
    if (activeAttachment) {
      document.addEventListener("keydown", handleKeyNavigation);
      return () => document.removeEventListener("keydown", handleKeyNavigation);
    }
  }, [activeAttachment, handleKeyNavigation]);

  useEffect(() => {
    if (activeAttachment && attachments.length > 0) {
      if (currentAttachmentIndex < 0) {
        const lastIndex = attachments.length - 1;
        setCurrentAttachmentIndex(lastIndex);
        setActiveAttachment(attachments[lastIndex]);
      } else {
        const safeIndex = Math.min(
          currentAttachmentIndex,
          attachments.length - 1,
        );
        setCurrentAttachmentIndex(safeIndex);
        setActiveAttachment(attachments[safeIndex]);
      }
    }
  }, [
    attachments,
    pagination?.currentPage,
    activeAttachment,
    currentAttachmentIndex,
  ]);

  const getFileIconComponent = (
    fileName: string,
    iconSize: "sm" | "md" | "lg" = "md",
  ) => {
    const fileType = getFileType(fileName);
    const sizeClass = {
      sm: "text-lg",
      md: "text-2xl",
      lg: "text-4xl",
    }[iconSize];

    switch (fileType) {
      case "image":
        return <ImageIcon className={`${sizeClass} text-blue-500`} />;
      case "pdf":
        return <PictureAsPdfIcon className={`${sizeClass} text-red-500`} />;
      default:
        return <AttachFileIcon className={`${sizeClass} text-gray-500`} />;
    }
  };

  const renderThumbnail = (attachment: Attachment, index: number) => {
    const fileType = getFileType(attachment.fileName);
    const displayName = getFileNameWithoutExtension(attachment.fileName);

    if (fileType === "image") {
      return (
        <figure
          key={index}
          className={`flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity ${sizeClasses[size]} relative group`}
          onClick={() => openAttachmentModal(attachment, index)}
        >
          <img
            src={attachment.url}
            alt={`${displayName}`}
            className="w-full h-full rounded-md shadow-md object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-md"></div>
        </figure>
      );
    }

    return (
      <div
        key={index}
        className={`flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity ${sizeClasses[size]} bg-gray-100 rounded-md shadow-md flex flex-col items-center justify-center p-2 relative group border-2 border-gray-200 hover:border-primary`}
        onClick={() => openAttachmentModal(attachment, index)}
      >
        <div className="mb-1">
          {getFileIconComponent(attachment.fileName, "md")}
        </div>
        <span
          className="text-xs text-center text-gray-700 font-medium leading-tight overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {displayName}
        </span>
        <span className="text-xs text-gray-500 uppercase mt-1">
          {attachment.fileName.split(".").pop()}
        </span>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-primary/10 transition-colors rounded-md"></div>
      </div>
    );
  };

  const renderModal = () => {
    if (!activeAttachment) return null;

    const fileType = getFileType(activeAttachment.fileName);
    const displayName = getFileNameWithoutExtension(activeAttachment.fileName);
    const hasMultipleAttachments =
      attachments.length > 1 || (pagination && pagination.totalPages > 1);
    const isFirstAttachment =
      currentAttachmentIndex === 0 &&
      (!pagination || pagination.currentPage === 1);
    const isLastAttachment =
      currentAttachmentIndex === attachments.length - 1 &&
      (!pagination || pagination.currentPage === pagination.totalPages);

    return (
      <div
        className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
        onClick={(e) => {
          // Fechar modal ao clicar no backdrop
          if (e.target === e.currentTarget) {
            setActiveAttachment(null);
          }
        }}
      >
        <div className="max-w-4xl w-full relative bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-hidden">
          {/* Setas de navegação */}
          {hasMultipleAttachments && (
            <>
              <button
                onClick={() => navigateAttachment("prev")}
                disabled={isFirstAttachment}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
              >
                <ChevronLeftIcon />
              </button>
              <button
                onClick={() => navigateAttachment("next")}
                disabled={isLastAttachment}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer"
              >
                <ChevronRightIcon />
              </button>
            </>
          )}
          {/* Header do modal */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              {getFileIconComponent(activeAttachment.fileName, "md")}
              <div>
                <h3 className="font-medium text-gray-900">{displayName}</h3>
                <p className="text-sm text-gray-500">
                  {activeAttachment.fileName}
                </p>
                {hasMultipleAttachments && (
                  <p className="text-xs text-gray-400">
                    {pagination
                      ? `${(pagination.pageSize || attachments.length) * (pagination.currentPage - 1) + currentAttachmentIndex + 1} de ${pagination.totalItems} (Página ${pagination.currentPage} de ${pagination.totalPages})`
                      : `${currentAttachmentIndex + 1} de ${attachments.length}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Download">
                <IconButton
                  onClick={() => handleDownload(activeAttachment)}
                  className="text-gray-600 hover:text-primary hover:cursor-pointer"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Fechar">
                <IconButton
                  onClick={() => setActiveAttachment(null)}
                  className="text-gray-600 hover:text-red-500 hover:cursor-pointer"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className="relative overflow-auto max-h-[calc(90vh-80px)]">
            {fileType === "image" ? (
              <img
                src={activeAttachment.url}
                alt={displayName}
                className="w-full max-h-[80vh] object-contain"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 p-8">
                <div className="mb-4">
                  {getFileIconComponent(activeAttachment.fileName, "lg")}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {displayName}
                </h3>
                <p className="text-gray-600 mb-4">
                  {fileType === "pdf"
                    ? "Clique no botão abaixo para baixar o PDF."
                    : "Este tipo de arquivo não pode ser visualizado diretamente."}
                </p>
                <button
                  onClick={() => handleDownload(activeAttachment)}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                >
                  {fileType === "pdf" ? "Baixar PDF" : "Fazer Download"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-row gap-4 items-center overflow-x-auto pb-2">
          {attachments.map((attachment, index) =>
            renderThumbnail(attachment, index),
          )}
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <Typography variant="caption" className="text-gray-500">
              {pagination.totalItems} anexo(s) no total
            </Typography>

            <Box className="flex items-center gap-2">
              <Tooltip title="Página anterior">
                <IconButton
                  onClick={() =>
                    pagination.onPageChange(pagination.currentPage - 1)
                  }
                  disabled={pagination.currentPage <= 1}
                  size="small"
                  className="text-gray-600 hover:text-primary disabled:text-gray-300 cursor-pointer"
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>

              <Typography variant="caption" className="px-2">
                {pagination.currentPage} de {pagination.totalPages}
              </Typography>

              <Tooltip title="Próxima página">
                <IconButton
                  onClick={() =>
                    pagination.onPageChange(pagination.currentPage + 1)
                  }
                  disabled={pagination.currentPage >= pagination.totalPages}
                  size="small"
                  className="text-gray-600 hover:text-primary disabled:text-gray-300 cursor-pointer"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        )}
      </div>

      {renderModal()}
    </>
  );
}

export default AttachmentPreview;
